import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'

const PurchaseSuccessScreen = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, signUp } = useAuth()
  const [isProcessing, setIsProcessing] = useState(true)
  const [error, setError] = useState('')
  const [showAccountForm, setShowAccountForm] = useState(false)
  
  // Account form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    const processPurchase = async () => {
      try {
        const sessionId = searchParams.get('session_id')
        console.log('Processing purchase for user:', user?.id, 'session:', sessionId)

        if (!sessionId) {
          setError('Invalid payment session')
          setIsProcessing(false)
          return
        }

        // Set premium access in localStorage
        localStorage.setItem('isPremium', 'true')
        localStorage.setItem('purchaseDate', new Date().toISOString())
        localStorage.setItem('stripeSessionId', sessionId)

        // Ensure tracking mode is set (default to cycle if not set)
        if (!localStorage.getItem('trackingMode')) {
          localStorage.setItem('trackingMode', 'cycle')
        }

        console.log('Premium access granted, localStorage updated')

        // Wait for processing effect
        await new Promise(resolve => setTimeout(resolve, 2000))
        setIsProcessing(false)
      } catch (error) {
        console.error('Error processing purchase:', error)
        setError(error.message || 'Failed to process purchase')
        setIsProcessing(false)
      }
    }

    processPurchase()
  }, [searchParams, user])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleCreateAccount = async () => {
    setLoading(true)
    setFormError('')

    // Validation
    if (!email?.trim() || !password?.trim() || !name?.trim()) {
      setFormError('Please fill in all fields.')
      setLoading(false)
      return
    }

    if (!validateEmail(email.trim())) {
      setFormError('Please enter a valid email address.')
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters long.')
      setLoading(false)
      return
    }

    try {
      const { data, error } = await signUp(email.trim(), password, {
        full_name: name.trim(),
        tracking_mode: 'cycle'
      })

      if (error) throw error

      // Success - redirect to premium dashboard
      navigate('/premium-dashboard')
    } catch (error) {
      console.error('Account creation error:', error)
      let errorMessage = 'Account creation failed. Please try again.'
      
      if (error.message) {
        errorMessage = error.message
        if (error.message.includes('An account with this email already exists')) {
          errorMessage = 'An account with this email already exists. Please use a different email or sign in instead.'
        }
      }
      
      setFormError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleContinueWithAccount = () => {
    // User already has account, go directly to premium dashboard
    navigate('/premium-dashboard')
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-white px-6 py-8 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="mb-6">
            <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
          <h2 className="italiana-font text-2xl text-black mb-4">
            Processing your payment...
          </h2>
          <p className="text-gray-600">
            We're setting up your premium access
          </p>
        </motion.div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white px-6 py-8 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center max-w-md mx-auto"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-2xl text-red-600">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-black mb-4">
            Payment Processing Error
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/unlock')}
              className="w-full gold-bg text-white py-3 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto text-center"
      >
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="mb-8"
        >
          <div className="w-24 h-24 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl text-white">🎉</span>
          </div>
        </motion.div>

        {/* Welcome Message */}
        <h1 className="italiana-font text-3xl text-black mb-6">
          🎉 Welcome to Cycle CEO Premium!
        </h1>

        <div className="text-left mb-8 space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Your payment went through perfectly - thank you for trusting us.
          </p>
          <p className="text-gray-700 leading-relaxed">
            <strong>From this moment on, you will work with your natural cycle instead of pushing against it.</strong>
          </p>
        </div>

        {/* Account Creation Required */}
        {user ? (
          // User already has account - can proceed directly
          <div className="space-y-6">
            <p className="text-gray-700 leading-relaxed mb-6">
              We're thrilled to guide you as you plan work, family life and self-care in perfect rhythm.
            </p>

            <motion.button
              onClick={handleContinueWithAccount}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Enter Your Premium Dashboard →
            </motion.button>
          </div>
        ) : (
          // No account - must create one to proceed
          <div className="space-y-6">
            <p className="text-gray-700 leading-relaxed text-left mb-6">
              Create your account so your premium access is saved forever and synced on every device.
            </p>

            {!showAccountForm ? (
              <motion.button
                onClick={() => setShowAccountForm(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity mb-6"
              >
                Create Your Account →
              </motion.button>
            ) : (
              // Account Creation Form
              <div className="space-y-6 text-left">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                    placeholder="Enter your first name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                    placeholder="Create a secure password"
                    minLength={6}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>

                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-600">{formError}</p>
                  </div>
                )}

                <motion.button
                  onClick={handleCreateAccount}
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating Account...</span>
                    </div>
                  ) : (
                    'Create Account & Access Premium →'
                  )}
                </motion.button>
              </div>
            )}

            {/* Benefits */}
            <div className="bg-gray-50 p-6 rounded-lg text-left">
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-3">
                  <span className="text-lg">🔐</span>
                  <span>Links your purchase securely to you - no lost access, ever</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-lg">📱</span>
                  <span>Lets you sign in on any device, any time</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-lg">⚡</span>
                  <span>Keeps your data backed up and your cycle insights safe</span>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed">
              We're thrilled to guide you as you plan work, family life and self-care in perfect rhythm.
            </p>
          </div>
        )}

        {/* Trust Message */}
        <div className="mt-8">
          <p className="text-xs text-gray-500">
            🔒 Your payment is secure and your premium access is immediately active.
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default PurchaseSuccessScreen